import React from "react";
import { Box, Button, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useTranslation } from "react-i18next";
import { Formik, Field, Form } from "formik";
import * as Yup from "yup";

// Custom
import ButtonGroup from "../../components/Buttons/ButtonGroup";
import RadioGroup from "../../components/FormikFields/RadioGroup";
import {
  useDeleteAndExclude,
  useDeleteExclusions
} from "../../hooks/userAssignments";
import { isValidDate, isBeforeDate } from "../../utils/datetime";
import { TextField } from "formik-mui";
import DatePickerField from "../../components/FormikFields/DatePickerField";
import { Tooltip } from "../../components/Tooltip";
import usePermissions from "../../redux/selectors/usePermissions";

const ExclusionForm = ({
  submitForm,
  values,
  label,
  rowData,
  deleteExclusion
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { isAdmin, notSaveEmployeeAssignments } = usePermissions();

  const disabled = () => {
    if (notSaveEmployeeAssignments) return true;
    else if (!isAdmin) return rowData.exclusion.data.attributes.excludedByAdmin;
    else return false;
  };

  return (
    <Form>
      <Box
        justifyContent="space-between"
        display="flex"
        alignItems="flex-end"
        pb={1.75}
      >
        <Box
          display="flex"
          flexDirection="column"
          minWidth={260}
          flex={2}
          ml={13.5}
        >
          <Box display="flex" alignItems="center" maxWidth={356}>
            <RadioGroup
              radioButtons={[
                { label: t("yes"), value: "true" },
                { label: t("no"), value: "false" }
              ]}
              disabled={disabled()}
              name="expires"
              label={t("courseExclusions.settingsModal.expires")}
            />
          </Box>
          {values.expires === "true" && (
            <Box display="flex" alignItems="center" mt={2}>
              <Typography sx={{ flex: "0 0 150px", mr: 1.5 }} variant="body2">
                {t("courseExclusions.settingsModal.expiresAt")}
              </Typography>
              <Box justifyContent="space-between">
                <Field
                  minDate={new Date()}
                  name="expiresAt"
                  component={DatePickerField}
                  disabled={disabled()}
                  InputClassName={classes.input}
                />
              </Box>
            </Box>
          )}
          <Box display="flex" alignItems="center" mt={2} maxWidth={600}>
            <Typography
              variant="body2"
              noWrap
              sx={{ flex: "0 0 150px", mr: 1.5 }}
            >
              {t("userAssignments.comments")}
            </Typography>
            <Field
              variant="filled"
              fullWidth
              component={TextField}
              disabled={disabled()}
              name="note"
              className={classes.formControl}
              InputProps={{ className: classes.input }}
              hiddenLabel
            />
          </Box>
        </Box>
        <Box flex={1} px={2} display="flex" justifyContent="center">
          {!notSaveEmployeeAssignments && (
            <ButtonGroup align="right">
              {label &&
                (disabled() ? (
                  <Tooltip title={t("userAssignments.disableButtonTooltip")}>
                    <div>
                      <Button
                        color="primary"
                        onClick={() => deleteExclusion([rowData])}
                        disabled={disabled()}
                      >
                        {t("userAssignments.cancelExclusion")}
                      </Button>
                    </div>
                  </Tooltip>
                ) : (
                  <Button
                    color="primary"
                    onClick={() => deleteExclusion([rowData])}
                    disabled={disabled()}
                  >
                    {t("userAssignments.cancelExclusion")}
                  </Button>
                ))}
              {disabled() ? (
                <Tooltip title={t("userAssignments.disableButtonTooltip")}>
                  <div>
                    <Button
                      color="primary"
                      variant="contained"
                      onClick={submitForm}
                      disabled={disabled()}
                    >
                      {t("userAssignments.save")}
                    </Button>
                  </div>
                </Tooltip>
              ) : (
                <Button
                  color="primary"
                  variant="contained"
                  onClick={submitForm}
                  disabled={disabled()}
                >
                  {t("userAssignments.save")}
                </Button>
              )}
            </ButtonGroup>
          )}
        </Box>
      </Box>
    </Form>
  );
};

const useStyles = makeStyles(theme => ({
  input: {
    backgroundColor: theme.palette.common.white,
    height: 40
  }
}));

const getCourseExclusionSettingsSchema = t =>
  Yup.object().shape({
    note: Yup.string()
      .max(
        150,
        t("validationMessages.cannotExceedLength", {
          type: t("courseVersion.details.title"),
          maxLength: 150
        })
      )
      .required(t("validationMessages.fieldRequired")),
    expiresAt: Yup.mixed().when("expires", {
      is: "true",
      then: Yup.mixed()
        .required(t("validationMessages.fieldRequired"))
        .test("expiresAt", t("validationMessages.validDate"), function() {
          return isValidDate(this.parent.expiresAt);
        })
        .test(
          "minDate",
          t("validationMessages.dateCannotBeInPast"),
          function() {
            return isBeforeDate(new Date(), this.parent.expiresAt);
          }
        )
    })
  });

const ExclusionDetail = ({
  rowData,
  onSubmit,
  presentDeleteCallback,
  presentUpdateCallback
}) => {
  const { t } = useTranslation();
  const { label } = useDeleteAndExclude(
    rowData.user.id,
    [rowData],
    presentDeleteCallback,
    presentUpdateCallback
  );

  const deleteExclusion = useDeleteExclusions(
    rowData.user.id,
    presentUpdateCallback
  );

  return (
    <Formik
      enableReinitialize={true}
      onSubmit={onSubmit}
      initialValues={{
        expires: rowData.exclusion.data.attributes.expiresAt ? "true" : "false",
        expiresAt: rowData.exclusion.data.attributes.expiresAt,
        note: rowData.exclusion.data.attributes.note,
        id: rowData.exclusion.data.attributes.id
      }}
      validationSchema={getCourseExclusionSettingsSchema(t)}
    >
      {({ submitForm, values }) => {
        return (
          <ExclusionForm
            submitForm={submitForm}
            values={values}
            label={label}
            rowData={rowData}
            deleteExclusion={deleteExclusion}
          />
        );
      }}
    </Formik>
  );
};

export default ExclusionDetail;
